<script setup lang="ts">
/**
 * The slim strip that tells the person something is pending on every page, with one way to act on it: that the
 * enterprise has no AI account yet and Step 1 is where it is connected, or that a plan limit is near or reached and
 * Billing is where it is lifted (#2122). It is the kit's only banner, so no screen draws its own strip or mixes its
 * own tint to say "heads up".
 *
 * One line, always. The message truncates before the strip ever grows a second row, so it never pushes the page
 * down by more than a line of system text. It is a status region: assistive tech reads it when it appears
 * without moving focus away from the page the person opened.
 *
 * The tone is a state of the status colour system (BG §18.4): action cyan for waiting on a human, which is exactly
 * what a missing account is; amber (`warning`) for a limit coming close; magenta (`blocked`) for one that has
 * stopped work. A glyph and the words carry the meaning too, so colour never carries it alone.
 *
 * `dismissible` is opt-in: it adds the kit's close button, labelled for assistive tech, which emits `dismiss`. The
 * caller decides what dismissing means and for how long; without it there is no close control at all.
 *
 * `fullText` is opt-in too, for a message long enough to be cut short (#2122): the whole message, readable however
 * the person reaches it. Hover or keyboard focus on the message shows it in the kit's `ZhTooltip`; a tap, a click,
 * or Enter or Space on the focused message lets the strip show it in full, over as many lines as it needs, until
 * the next tap. That is the one time the strip grows: when the person asks to read the rest.
 */
import { XIcon } from '@lucide/vue'
import { ref, watch } from 'vue'
import { Button } from './ui/button'
import ZhTooltip from './ZhTooltip.vue'

const props = withDefaults(
    defineProps<{
        /**
         * `action` is waiting on the person (cyan); `system` is the console speaking about itself; `warning` is a
         * limit coming close (amber); `blocked` is a limit that has stopped work (magenta).
         */
        tone?: 'action' | 'system' | 'warning' | 'blocked'
        /** Offers a close button that emits `dismiss`. */
        dismissible?: boolean
        /** The whole message, for when the one line cuts it short: a tooltip, and a tap to show it in full. */
        fullText?: string | null
    }>(),
    { tone: 'action', dismissible: false, fullText: null },
)

const emit = defineEmits<{ dismiss: [] }>()

/** Whether the person has asked to read the whole message in the strip itself. */
const expanded = ref(false)

// Another message starts on its one line again.
watch(
    () => props.fullText,
    () => {
        expanded.value = false
    },
)

function toggleFull(): void {
    expanded.value = !expanded.value
}
</script>

<template>
	<div class="zh-banner" role="status" :data-tone="tone">
		<span class="zh-banner__glyph" aria-hidden="true">▲</span>
		<ZhTooltip v-if="fullText" :text="fullText">
			<span
				class="zh-banner__message"
				data-full-text
				tabindex="0"
				role="button"
				:aria-expanded="expanded"
				:data-expanded="expanded || undefined"
				@click="toggleFull"
				@keydown.enter.prevent="toggleFull"
				@keydown.space.prevent="toggleFull"
				><slot
			/></span>
		</ZhTooltip>
		<span v-else class="zh-banner__message"><slot /></span>
		<span v-if="$slots.action" class="zh-banner__action"><slot name="action" /></span>
		<Button
			v-if="dismissible"
			type="button"
			variant="ghost"
			size="icon-xs"
			class="zh-banner__dismiss"
			aria-label="Dismiss"
			@click="emit('dismiss')"
		>
			<XIcon aria-hidden="true" />
		</Button>
	</div>
</template>

<style scoped>
.zh-banner {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
    white-space: nowrap;
    border-bottom: 1px solid color-mix(in srgb, var(--state-action) 50%, var(--border));
    background: color-mix(in srgb, var(--state-action) 8%, var(--system-charcoal));
    color: var(--foreground);
    padding: 0.375rem 1rem;
    font-size: 0.8125rem;
    line-height: 1.4;
}

.zh-banner[data-tone='system'] {
    border-bottom-color: color-mix(in srgb, var(--state-system) 50%, var(--border));
    background: color-mix(in srgb, var(--state-system) 8%, var(--system-charcoal));
}

.zh-banner[data-tone='warning'] {
    border-bottom-color: color-mix(in srgb, var(--state-active) 50%, var(--border));
    background: color-mix(in srgb, var(--state-active) 8%, var(--system-charcoal));
}

.zh-banner[data-tone='blocked'] {
    border-bottom-color: color-mix(in srgb, var(--state-failed) 60%, var(--border));
    background: color-mix(in srgb, var(--state-failed) 12%, var(--system-charcoal));
}

.zh-banner__glyph {
    flex-shrink: 0;
    font-size: 0.625rem;
    color: var(--state-action);
}

.zh-banner[data-tone='system'] .zh-banner__glyph {
    color: var(--state-system);
}

.zh-banner[data-tone='warning'] .zh-banner__glyph,
.zh-banner[data-tone='warning'] .zh-banner__action :deep(a) {
    color: var(--state-active);
}

.zh-banner[data-tone='blocked'] .zh-banner__glyph,
.zh-banner[data-tone='blocked'] .zh-banner__action :deep(a) {
    color: var(--state-failed);
}

.zh-banner__message {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
}

.zh-banner__message[data-full-text] {
    cursor: pointer;
}

.zh-banner__message[data-full-text]:focus-visible {
    outline: 1px solid var(--ring);
    outline-offset: 2px;
}

/* Asked for: the whole message, over as many lines as it needs. */
.zh-banner__message[data-expanded] {
    white-space: normal;
    overflow: visible;
}

.zh-banner__action {
    flex-shrink: 0;
    margin-left: auto;
    font-family: var(--font-mono);
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
    font-size: 0.75rem;
}

.zh-banner__action :deep(a) {
    color: var(--state-action);
    text-decoration: underline;
    text-underline-offset: 0.2em;
}

.zh-banner__dismiss {
    flex-shrink: 0;
}

.zh-banner__action + .zh-banner__dismiss {
    margin-left: 0;
}

.zh-banner__message + .zh-banner__dismiss {
    margin-left: auto;
}
</style>
